import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/app/AppShell";

export default function Settings() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "agent")) { navigate("/profile", { replace: true }); return; }
    (async () => {
      try { const data = await api("/compensation/grid"); setTiers(data.tiers); }
      catch (e) { alert(e.message); }
      finally { setLoading(false); }
    })();
  }, [user, navigate, authLoading]);

  const updateRow = (ti, ri, field, val) => {
    setTiers((prev) => {
      const next = prev.map((t) => ({ ...t, rows: [...t.rows] }));
      const num = field === "max" && val === "" ? null : parseInt(String(val).replace(/[^0-9]/g, ""), 10) || 0;
      next[ti].rows[ri][field] = num;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try { await api("/settings/grid", { method: "PUT", body: { tiers } }); alert("Saved! Compensation rates updated."); }
    catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <AppShell><div className="grid place-items-center py-24"><Loader2 className="animate-spin" color="#D62828" /></div></AppShell>;

  return (
    <AppShell>
      <div className="px-5 lg:px-10 py-8 max-w-3xl">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-10 h-10 grid place-items-center -ml-2 rounded-lg hover:bg-[#F5F5F5]"><ArrowLeft size={20} /></button>
        <span className="eyebrow mt-4 block">ADMIN SETTINGS</span>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">Compensation Rates</h1>
        <p className="text-sm mt-2 max-w-lg" style={{ color: "#5A5A5A" }}>Edit per-tier per-case rates. Volume thresholds are based on each partner's monthly paid count.</p>

        <div className="mt-6 space-y-3 pb-24">
          {tiers.map((t, ti) => (
            <div key={t.tier} className="bg-white rounded-2xl p-5 border" style={{ borderColor: "#E8E8E8" }}>
              <h3 className="font-display font-black mb-3">{t.label || `Tier ${t.tier}`}</h3>
              <div className="flex gap-2 px-1 mb-1.5">
                <span className="flex-1 text-[10px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>FROM</span>
                <span className="flex-1 text-[10px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>TO</span>
                <span className="flex-[1.2] text-[10px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>$/CASE</span>
              </div>
              {t.rows.map((r, ri) => (
                <div key={ri} className="flex gap-2 mb-1.5">
                  <input data-testid={`tier-${t.tier}-row-${ri}-min`} type="number" value={r.min} onChange={(e) => updateRow(ti, ri, "min", e.target.value)} className="input-field !py-2 flex-1 !font-extrabold" />
                  <input data-testid={`tier-${t.tier}-row-${ri}-max`} type="number" value={r.max === null ? "" : r.max} onChange={(e) => updateRow(ti, ri, "max", e.target.value)} placeholder="∞" className="input-field !py-2 flex-1 !font-extrabold" />
                  <div className="flex-[1.2] flex items-center gap-1 px-3 rounded-xl" style={{ background: "#F5F5F5" }}>
                    <span className="font-extrabold" style={{ color: "#5A5A5A" }}>$</span>
                    <input data-testid={`tier-${t.tier}-row-${ri}-rate`} type="number" value={r.rate} onChange={(e) => updateRow(ti, ri, "rate", e.target.value)} className="bg-transparent outline-none flex-1 font-extrabold py-2" />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="fixed bottom-0 left-0 right-0 lg:left-72 px-5 lg:px-10 py-4 bg-white border-t" style={{ borderColor: "#E8E8E8" }}>
          <div className="max-w-3xl">
            <button data-testid="save-grid-btn" onClick={save} disabled={saving} className="btn-primary w-full !py-3.5">
              {saving ? <Loader2 className="animate-spin" size={18} /> : (<><Save size={18} /> Save Rates</>)}
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
